import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import AddNewRole from "../components/roles/AddNewRole";
import ViewRole from "../components/roles/ViewRole";
import UpdateRole from "../components/roles/UpdateRole";
import DeleteRole from "../components/roles/DeleteRole";
import { useDispatch, useSelector } from "react-redux";
import { fetchRoles } from "../redux/features/roles/rolesSlice";
import { ToastContainer } from "react-toastify";
import { ShimmerBadge, ShimmerTable } from "react-shimmer-effects";

function Roles() {
  const dispatch = useDispatch();
  const { roles, getRolesLoading } = useSelector((state) => state.roles);
  const [currentPage, setCurrentPage] = useState(1);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };
  const filteredRoles =
    roles?.filter((role) =>
      role?.name?.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || [];

  const totalPages = Math.ceil(filteredRoles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentRoles = filteredRoles.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const refetchRoles = async () => {
    await dispatch(fetchRoles());
  };

  if (getRolesLoading) {
    return (
      <div className="min-h-screen bg-lightPurple/5 flex flex-col">
        <ToastContainer />
        <Navbar />
        <div className="flex-grow p-4 grid grid-cols-1 max-w-7xl w-full mx-auto">
          <div className="my-6 flex justify-between items-center">
            <h1 className="text-4xl font-bold leading-tight">Roles</h1>
            <div className="flex items-center">
              <AddNewRole refetchRoles={refetchRoles} />
            </div>
          </div>
          <div className="p-8 bg-gray-50 rounded-xl shadow-lg">
            <div className="flex justify-end mb-8">
              <ShimmerBadge width={250} />
            </div>
            <ShimmerTable row={5} col={4} gap={20} />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-lightPurple/5 flex flex-col">
      <ToastContainer />
      <Navbar />
      <div className="flex-grow grid grid-cols-1 max-w-7xl mx-auto w-full">
        <div className="flex justify-between items-center mt-4 mb-6">
          <h1 className="text-4xl font-bold">Roles</h1>
          <AddNewRole refetchRoles={refetchRoles} />
        </div>

        <div className="mb-[4.5rem] overflow-x-auto">
          <div className="p-8 bg-gray-50 rounded-xl shadow-lg">
            <div className="flex justify-end mb-8">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-72 border border-gray-300 rounded-lg p-2 pl-4 shadow-sm focus:ring-2 focus:ring-purpleColor focus:outline-none transition"
              />
            </div>

            <div className="overflow-x-auto rounded-lg shadow-md">
              <table
                className="min-w-full bg-white text-center border border-gray-200 rounded-xl"
                style={{ scrollbarWidth: "thin" }}
              >
                <thead className="bg-gradient-to-r from-lightPurple to-purpleColor text-white uppercase text-base font-semibold">
                  <tr>
                    <th className="p-4">ID</th>
                    <th className="p-4">Name</th>
                    <th className="p-4">Guard Name</th>
                    <th className="p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRoles.length > 0 ? (
                    currentRoles?.map((role) => (
                      <tr key={role.id} className="hover:bg-gray-50 transition">
                        <td className="p-4 text-gray-600">{role.id}</td>

                        <td className="p-4 font-medium">{role.name}</td>
                        <td className="p-4 font-medium">{role.guard_name}</td>

                        <td className="p-4">
                          <div className="flex justify-center gap-3">
                            <ViewRole roleId={role.id} />
                            <UpdateRole
                              roleId={role.id}
                              refetchRoles={refetchRoles}
                            />
                            <DeleteRole
                              roleId={role.id}
                              refetchRoles={refetchRoles}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="4"
                        className="p-10 text-gray-500 font-medium"
                      >
                        No Roles found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row justify-between items-center mt-12 gap-4">
              <div className="flex items-center gap-2">
                <span className="text-gray-700 font-medium">
                  Rows per page:
                </span>
                <select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  className="border border-gray-300 rounded-md px-2 py-1 bg-white shadow-sm focus:ring-2 focus:ring-purpleColor focus:outline-none transition"
                >
                  <option value={5}>5</option>
                  <option value={20}>20</option>
                  <option value={30}>30</option>
                </select>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium border transition ${
                    currentPage === 1
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-white hover:bg-purple-50 text-purpleColor border-purpleColor"
                  }`}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                  Prev
                </button>

                <span className="text-gray-700 font-medium">
                  Page {currentPage} of {totalPages || 1}
                </span>

                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition ${
                    currentPage === totalPages
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-lightPurple text-white hover:bg-purpleColor"
                  }`}
                >
                  Next
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default Roles;
